import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useRecoilState } from "recoil";
import { Avatar, Box, CircularProgress, Typography } from "@mui/material";
import AppButton from "../../components/AppButton";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { currentUserAtom } from "../../AuthProvider";
import { checkErrors, getDefaultOptions } from "../../services/Category";
import { stringAvatar } from "../../Layout/Menu/utils";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png"];

const uploadProfilePicture = async (file: File) => {
  const url = new URL("/api/users/upload_picture/", window.origin);
  const formData = new FormData();
  formData.append("picture", file);

  const options = getDefaultOptions({ method: "POST", body: formData }, true);
  const response = await fetch(url.toString(), options);
  checkErrors(response);
  return response.json();
};

const deleteProfilePicture = async () => {
  const url = new URL("/api/users/delete_picture/", window.origin);
  const options = getDefaultOptions({ method: "POST" });
  const response = await fetch(url.toString(), options);
  checkErrors(response);
  return response.json();
};

const validateFile = (file: File): string | null => {
  const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;
  if (!ALLOWED_EXTENSIONS.includes(extension)) {
    return "PROFILE_PICTURE_INVALID_FORMAT";
  }
  if (file.size > MAX_FILE_SIZE) {
    return "PROFILE_PICTURE_TOO_LARGE";
  }
  return null;
};

const ProfilePictureSection = () => {
  const intl = useIntl();
  const [currentUser, setCurrentUser] = useRecoilState(currentUserAtom);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasPicture = !!currentUser?.profile_picture_url;

  const processFile = useCallback(
    async (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setError(intl.formatMessage({ id: validationError }));
        return;
      }

      setError(null);
      setLoading(true);
      try {
        const previewUrl = URL.createObjectURL(file);
        await uploadProfilePicture(file);
        setCurrentUser((prev) =>
          prev
            ? {
                ...prev,
                profile_picture_url: previewUrl,
              }
            : prev
        );
      } catch {
        setError(intl.formatMessage({ id: "PROFILE_PICTURE_UPLOAD_ERROR" }));
      } finally {
        setLoading(false);
      }
    },
    [intl, setCurrentUser]
  );

  const handleDelete = async () => {
    setError(null);
    setLoading(true);
    try {
      await deleteProfilePicture();
      setCurrentUser((prev) =>
        prev
          ? {
              ...prev,
              profile_picture_url: null,
            }
          : prev
      );
    } catch {
      setError(intl.formatMessage({ id: "PROFILE_PICTURE_DELETE_ERROR" }));
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClick = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".jpg,.jpeg,.png";
    input.addEventListener("change", () => {
      const file = input.files?.[0];
      if (file) {
        processFile(file);
      }
    });
    input.click();
  }, [processFile]);

  const fullName = currentUser
    ? `${currentUser.first_name} ${currentUser.last_name}`
    : "";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1 }}>
      <Avatar
        alt={fullName}
        src={currentUser?.profile_picture_url ?? undefined}
        {...(!hasPicture ? stringAvatar(fullName) : {})}
        sx={(theme) => ({
          width: 64,
          height: 64,
          fontSize: "1.5rem",
          fontWeight: "bold",
          color: theme.vars.palette.common.white,
          background: hasPicture
            ? "transparent"
            : theme.vars.palette.nancie.gradient,
        })}
      />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              {hasPicture ? (
                <>
                  <AppButton
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={handleUploadClick}
                  >
                    {intl.formatMessage({ id: "PROFILE_PICTURE_CHANGE" })}
                  </AppButton>
                  <AppButton
                    intent="danger"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={handleDelete}
                  >
                    {intl.formatMessage({ id: "PROFILE_PICTURE_DELETE" })}
                  </AppButton>
                </>
              ) : (
                <AppButton
                  size="small"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleUploadClick}
                >
                  {intl.formatMessage({ id: "PROFILE_PICTURE_UPLOAD" })}
                </AppButton>
              )}
            </>
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
          }}
        >
          {intl.formatMessage({ id: "PROFILE_PICTURE_HINT" })}
        </Typography>
        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ProfilePictureSection;
